(() => {
  'use strict';

  const ELEMENT_NAME = 'lfm-newsletter-popup';
  const DEFAULT_DELAY_SECONDS = 4;
  const DEFAULT_SCROLL_PERCENT = 50;
  const DEFAULT_FREQUENCY_DAYS = 3;
  const PENDING_TTL_MS = 30 * 60 * 1000;
  const SUBSCRIBED_TTL_DAYS = 365;
  const COOKIE_NAME_PREFIX = 'lfnp_pending_';
  const MEMORY_SESSION = Object.create(null);
  const RUNTIME_KEY = '__lfnpNativeRuntime';
  const VISITOR_KEY = 'lfnp:visitor:v1';
  const VISIT_KEY = 'lfnp:views:v1';
  const EXPERIMENT_KEY = 'lfnp:experiments:v1';

  const asBoolean = (value) => value === true || value === 'true' || value === '1';

  const finiteNumber = (value, fallback, minimum, maximum) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(maximum, Math.max(minimum, parsed));
  };

  const isVisible = (element) => {
    if (!element || !element.isConnected) return false;
    if (element.hidden || element.getAttribute('aria-hidden') === 'true' || element.hasAttribute('inert')) return false;
    // Browsers can report a non-zero box for descendants of a closed <details> even though
    // those descendants are clipped from view. Ignore those controls in focus traps and modal
    // checks, while keeping controls inside the visible summary eligible.
    for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
      if (ancestor.localName !== 'details' || ancestor.open) continue;
      const summary = Array.from(ancestor.children).find((child) => child.localName === 'summary');
      if (!summary || !summary.contains(element)) return false;
    }
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  const safeFocus = (element) => {
    if (!element || typeof element.focus !== 'function' || !element.isConnected) return;
    try {
      element.focus({ preventScroll: true });
    } catch (_error) {
      element.focus();
    }
  };

  const readStorage = (storage, key) => {
    try {
      return storage ? storage.getItem(key) : null;
    } catch (_error) {
      return null;
    }
  };

  const writeStorage = (storage, key, value) => {
    try {
      if (!storage) return false;
      storage.setItem(key, value);
      return true;
    } catch (_error) {
      return false;
    }
  };

  const removeStorage = (storage, key) => {
    try {
      if (storage) storage.removeItem(key);
    } catch (_error) {
      // Private browsing and storage quotas can make removal fail. Runtime state remains usable.
    }
  };

  const getCookie = (name) => {
    try {
      const prefix = `${encodeURIComponent(name)}=`;
      const item = document.cookie.split('; ').find((entry) => entry.indexOf(prefix) === 0);
      return item ? decodeURIComponent(item.slice(prefix.length)) : null;
    } catch (_error) {
      return null;
    }
  };

  const setCookie = (name, value, maxAge) => {
    try {
      document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
    } catch (_error) {
      // Cookies can be disabled; sessionStorage or the page-local fallback may still work.
    }
  };

  const deleteCookie = (name) => setCookie(name, '', 0);

  const parseRecord = (raw) => {
    if (!raw) return null;
    try {
      const record = JSON.parse(raw);
      return record && typeof record === 'object' ? record : null;
    } catch (_error) {
      return null;
    }
  };

  const getStorage = (storageName) => {
    try {
      return storageName === 'local' ? window.localStorage : window.sessionStorage;
    } catch (_error) {
      return null;
    }
  };

  const readJsonStorage = (storageName, key, fallback) => parseRecord(readStorage(getStorage(storageName), key)) || fallback;

  const writeJsonStorage = (storageName, key, value) => writeStorage(getStorage(storageName), key, JSON.stringify(value));

  const splitValues = (value) => {
    if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
    return String(value || '').split(/[\n,]/).map((item) => item.trim()).filter(Boolean);
  };

  const wildcardMatches = (value, pattern) => {
    const normalizedPattern = String(pattern || '').trim();
    if (!normalizedPattern) return false;
    const escaped = normalizedPattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
    try {
      return new RegExp(`^${escaped}$`).test(value);
    } catch (_error) {
      return false;
    }
  };

  const parseExplicitDate = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return { value: null, valid: true };
    // An explicit timezone is required so the storefront does not silently use visitor time.
    if (!/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)) return { value: null, valid: false };
    const timestamp = Date.parse(raw);
    return { value: Number.isFinite(timestamp) ? timestamp : null, valid: Number.isFinite(timestamp) };
  };

  const hashString = (value) => {
    let hash = 2166136261;
    const input = String(value || '');
    for (let index = 0; index < input.length; index += 1) {
      hash ^= input.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  };

  const getRuntime = () => {
    if (window[RUNTIME_KEY]) return window[RUNTIME_KEY];
    const runtime = {
      roots: new Set(),
      autoConsumed: false,
      retryTimer: null,
      viewCount: 1,
      visitorSeed: null,
      initialized: false,
      register(root) {
        if (!this.initialized) this.initialize(Boolean(root?.preview));
        this.roots.add(root);
        queueMicrotask(() => {
          this.syncLaunchers();
          this.requestReadyCampaigns();
        });
      },
      unregister(root) {
        this.roots.delete(root);
        if (!this.roots.size) this.autoConsumed = false;
        if (this.retryTimer !== null && !this.roots.size) {
          window.clearTimeout(this.retryTimer);
          this.retryTimer = null;
        }
      },
      clearOtherPending(owner) {
        const ownSessionKey = owner?.pendingKey || '';
        const ownCookieName = owner?.pendingCookie || '';

        // Active roots can clear their own cookie/memory aliases. The storage sweep also
        // covers a campaign that is not rendered on this page after an editor change.
        this.roots.forEach((root) => {
          if (root !== owner && typeof root.clearPending === 'function') root.clearPending();
        });
        Object.keys(MEMORY_SESSION).forEach((key) => {
          if (key.indexOf('lfnp:pending:') === 0 && key !== ownSessionKey) delete MEMORY_SESSION[key];
        });

        try {
          const storage = window.sessionStorage;
          for (let index = storage.length - 1; index >= 0; index -= 1) {
            const key = storage.key(index);
            if (key && key.indexOf('lfnp:pending:') === 0 && key !== ownSessionKey) storage.removeItem(key);
          }
        } catch (_error) {
          // Storage can be unavailable in private browsing; the active roots were still cleared.
        }

        try {
          document.cookie.split(';').forEach((entry) => {
            const rawName = entry.trim().split('=')[0];
            let name = rawName;
            try { name = decodeURIComponent(rawName); } catch (_error) { /* keep raw name */ }
            if (name.indexOf(COOKIE_NAME_PREFIX) === 0 && name !== ownCookieName) deleteCookie(name);
          });
        } catch (_error) {
          // Cookie access can be disabled; sessionStorage/memory remain best-effort fallbacks.
        }
      },
      initialize(previewOnly = false) {
        if (this.initialized) return;
        this.initialized = true;
        if (previewOnly) {
          // Theme editor previews must remain ephemeral: no visit/visitor/experiment state is
          // written and no preview interaction can affect the storefront allocation.
          this.previewOnly = true;
          this.viewCount = 1;
          this.visitorType = 'new';
          this.visitorSeed = `preview-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
          return;
        }
        const storedVisitor = readJsonStorage('local', VISITOR_KEY, null);
        const storedViews = readJsonStorage('session', VISIT_KEY, null);
        const previousViewCount = Number(storedViews?.count);
        this.viewCount = Number.isFinite(previousViewCount) && previousViewCount > 0 ? previousViewCount + 1 : 1;

        // Keep new/returning classification in the session view record. A persistent
        // firstSeen flag only answers whether this browser has ever visited; consulting
        // it on every page would turn a new visitor into returning on page two and make
        // visitor=new + minViews targeting unreachable. A missing session record marks a
        // genuinely new session, so an existing local seed is then returning. If session
        // storage is unavailable, retain a page-local "new" fallback rather than making
        // a cross-page claim that cannot be persisted.
        const storedSessionType = storedViews?.visitorType;
        const hasSessionType = storedSessionType === 'new' || storedSessionType === 'returning';
        const hasPersistentVisitor = Boolean(storedVisitor?.seed || storedVisitor?.firstSeen);
        const sessionVisitorType = hasSessionType
          ? storedSessionType
          : (hasPersistentVisitor ? 'returning' : 'new');
        const sessionWritten = writeJsonStorage('session', VISIT_KEY, {
          count: this.viewCount,
          at: Date.now(),
          visitorType: sessionVisitorType,
        });
        this.visitorType = (sessionWritten || hasSessionType) ? sessionVisitorType : 'new';
        if (storedVisitor?.seed) {
          this.visitorSeed = String(storedVisitor.seed);
        } else {
          const seed = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
          this.visitorSeed = seed;
          writeJsonStorage('local', VISITOR_KEY, { seed, firstSeen: Date.now() });
        }
      },
      requestReadyCampaigns() {
        const ready = Array.from(this.roots)
          .filter((root) => !root.isInline() && root._autoReady && root.canAutoOpen())
          .sort((left, right) => {
            const priorityDifference = right.priority() - left.priority();
            return priorityDifference || left.campaign.localeCompare(right.campaign);
          });
        const winner = ready[0];
        if (!winner || this.autoConsumed) return;
        if (winner.isBlockedByThemeUi()) {
          if (this.retryTimer === null) {
            this.retryTimer = window.setTimeout(() => {
              this.retryTimer = null;
              this.requestReadyCampaigns();
            }, 1000);
          }
          return;
        }
        if (this.isInlineEditing()) {
          if (this.retryTimer === null) {
            this.retryTimer = window.setTimeout(() => {
              this.retryTimer = null;
              this.requestReadyCampaigns();
            }, 1000);
          }
          return;
        }
        this.autoConsumed = true;
        if (!winner.open(undefined, { automatic: true })) {
          this.autoConsumed = false;
          if (this.retryTimer === null) {
            this.retryTimer = window.setTimeout(() => {
              this.retryTimer = null;
              this.requestReadyCampaigns();
            }, 1000);
          }
        }
      },
      consumeAutomaticOpportunity(root) {
        // A trusted inline return is a page result, so reserve the shared automatic slot
        // before any later campaign can register and queue its own readiness check. Manual
        // launchers intentionally do not consult this reservation.
        if (!root || !root.isInline() || root.preview || this.previewOnly) return;
        this.autoConsumed = true;
      },
      isInlineEditing() {
        const active = document.activeElement;
        if (!(active instanceof Element)) return false;
        const root = active.closest(ELEMENT_NAME);
        if (!root || !root.isInline() || !root.isInlinePanelInViewport()) return false;
        return Boolean(active.closest('form')) &&
          (active.matches('input, select, textarea, button, [contenteditable="true"]') || active.isContentEditable);
      },
      markAutomaticDismissed() {
        this.autoConsumed = true;
        this.roots.forEach((root) => {
          if (!root.isOpen()) root.clearAutoTrigger();
        });
      },
      openManual(root, trigger) {
        if (!root || root.isInline()) return false;
        this.syncLaunchers();
        const winner = Array.from(this.roots)
          .filter((candidate) => !candidate.isInline() && candidate.canManualOpen())
          .sort((left, right) => right.priority() - left.priority() || left.campaign.localeCompare(right.campaign))[0];
        if (winner !== root) return false;
        this.roots.forEach((candidate) => {
          if (candidate !== root && candidate.isOpen()) {
            candidate.closeReason = 'campaign';
            candidate.close();
          }
        });
        const opened = root.isNonModal() && root.flow !== 'announcement'
          ? root.openBannerForm(trigger)
          : root.open(trigger, { manual: true });
        if (opened) {
          this.autoConsumed = true;
          this.roots.forEach((candidate) => {
            if (candidate !== root && !candidate.isOpen()) candidate.clearAutoTrigger();
          });
        }
        return opened;
      },
      syncLaunchers() {
        const winner = Array.from(this.roots)
          .filter((root) => !root.isInline() && root.canManualOpen())
          .sort((left, right) => right.priority() - left.priority() || left.campaign.localeCompare(right.campaign))[0];
        this.roots.forEach((root) => root.setLauncherVisibility(root === winner));
      },
      experimentAssignment(experiment, variants) {
        const name = String(experiment || '').trim();
        if (!name) return null;
        const storedRecords = readJsonStorage('local', EXPERIMENT_KEY, {});
        const records = Object.create(null);
        if (storedRecords && typeof storedRecords === 'object') {
          Object.keys(storedRecords).forEach((key) => { records[key] = storedRecords[key]; });
        }
        const existing = Object.prototype.hasOwnProperty.call(records, name) ? records[name] : null;
        const options = Array.from(variants || [])
          .map((item) => ({ variant: String(item.variant || 'A'), weight: Math.max(0, Number(item.weight) || 0) }))
          .sort((left, right) => left.variant.localeCompare(right.variant));
        const unique = [];
        options.forEach((item) => {
          if (!unique.some((option) => option.variant === item.variant)) unique.push(item);
        });
        const weightedTotal = unique.reduce((total, item) => total + item.weight, 0);
        if (!unique.length || weightedTotal <= 0) return null;
        if (existing && unique.some((item) => item.variant === existing.variant && item.weight > 0)) return existing.variant;
        const target = hashString(`${this.visitorSeed || 'page'}:${name}`) % weightedTotal;
        let cursor = 0;
        let assigned = unique[unique.length - 1].variant;
        for (const option of unique) {
          cursor += option.weight;
          if (target < cursor) {
            assigned = option.variant;
            break;
          }
        }
        records[name] = { variant: assigned, at: Date.now() };
        writeJsonStorage('local', EXPERIMENT_KEY, records);
        return assigned;
      },
    };
    window[RUNTIME_KEY] = runtime;
    return runtime;
  };

  const getFirstFocusable = (dialog) => dialog?.querySelector(
    'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
  );

  class LfmNewsletterPopup extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.timer = null;
      this.retryTimer = null;
      this.scrollHandler = null;
      this.previousFocus = null;
      this.previousOverflow = null;
      this.fallbackOpen = false;
      this.nonModalOpen = false;
      this.modalOpenForBanner = false;
      this.autoSuppressed = false;
      this.handledResult = false;
      this._autoReady = false;
      this._automaticOpen = false;
      this.closeReason = null;
      this.stepName = null;
      this.idleTimer = null;
      this.countdownTimer = null;
      if (typeof this.inlineImpressionRecorded !== 'boolean') this.inlineImpressionRecorded = false;
      if (typeof this.inlineResultRevealed !== 'boolean') this.inlineResultRevealed = false;
      if (typeof this.inlineImpressionRetryTimer !== 'number') this.inlineImpressionRetryTimer = null;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-form]');
      this.signup = this.querySelector('[data-signup]');
      this.config = this.readConfig();
      this.configInvalid = this.config === null;
      if (this.configInvalid) this.config = {};
      this.runtime = getRuntime();
      this.preview = asBoolean(this.dataset.designMode) || Boolean(window.Shopify?.designMode);
      this.campaign = String(this.dataset.campaign || 'welcome').trim() || 'welcome';
      this.localKey = `lfnp:${this.campaign}`;
      this.pendingKey = `lfnp:pending:${this.campaign}`;
      this.pendingCookie = `${COOKIE_NAME_PREFIX}${this.campaign.replace(/[^a-z0-9_-]/gi, '_').slice(0, 80)}`;
      this.runtime.register(this);
      this.initializeAdvancedContent();

      this.setupControls();
      this.installCompatibilityHook();
      this.prepareServerResult();

      if (!this.dialog && !this.isNonModal() && !this.isInline()) return;
      if (this.hasRenderableResult()) {
        if (this.isInline()) this.showInlineResult();
        else this.openWhenAvailable();
        return;
      }
      // A/B eligibility must be evaluated after every root in the current synchronous DOM
      // insertion batch has registered. Evaluating here would allocate the first variant before
      // its siblings exist and would make the DOM order permanently win the experiment.
      queueMicrotask(() => {
        if (!this.isConnected || this.autoSuppressed) {
          if (this.isInline()) this.setInlineVisibility(false);
          return;
        }
        if (this.isInline()) {
          const eligible = this.preview || this.isTargetingEligible();
          this.setInlineVisibility(eligible);
          if (eligible) this.setupInlineImpressionObserver();
          return;
        }
        if (this.preview || !this.isTargetingEligible()) return;
        this.scheduleAutoTrigger();
        this.runtime.syncLaunchers();
      });
    }

    readConfig() {
      const node = this.querySelector('script[type="application/json"][data-lfnp-config]');
      if (!node) return {};
      try {
        const parsed = JSON.parse(node.textContent || '{}');
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
      } catch (_error) {
        return null;
      }
    }

    configValue(key, fallback) {
      if (this.config && Object.prototype.hasOwnProperty.call(this.config, key)) return this.config[key];
      if (typeof this.dataset[key] !== 'undefined') return this.dataset[key];
      return fallback;
    }

    contextValue(key, fallback) {
      const context = this.config?.context;
      if (context && typeof context === 'object' && context[key] !== undefined) return context[key];
      if (this.dataset[key] !== undefined) return this.dataset[key];
      if (key === 'pageType') return this.dataset.pageType || document.body?.className?.match(/(?:^|\s)template-([\w-]+)/)?.[1] || fallback;
      if (key === 'language') return document.documentElement?.lang || fallback;
      if (key === 'country') return window.Shopify?.country || fallback;
      if (key === 'customerLoggedIn') return asBoolean(window.Shopify?.customer?.id);
      if (key === 'cartItems') {
        const count = document.querySelector('[data-cart-count]')?.textContent?.trim();
        return count || fallback;
      }
      return fallback;
    }

    formatName() {
      const value = String(this.configValue('format', 'modal') || 'modal').toLowerCase();
      return ['modal', 'drawer', 'banner', 'embed'].includes(value) ? value : 'modal';
    }

    flowName() {
      const value = String(this.configValue('flow', 'email') || 'email').toLowerCase();
      return ['email', 'two_step', 'announcement'].includes(value) ? value : 'email';
    }

    triggerName() {
      const value = String(this.configValue('trigger', 'delay') || 'delay').toLowerCase();
      return ['delay', 'scroll', 'manual', 'exit', 'idle'].includes(value) ? value : 'delay';
    }

    priority() {
      return finiteNumber(this.configValue('priority', 0), 0, -1000000, 1000000);
    }

    initializeAdvancedContent() {
      this.format = this.formatName();
      this.flow = this.flowName();
      this.trigger = this.isInline() ? 'manual' : this.triggerName();
      this.experimentName = this.isInline() ? '' : String(this.configValue('experiment', '') || '').trim();
      this.experimentVariant = String(this.configValue('variant', 'A') || 'A').trim() || 'A';
      this.experimentWeight = finiteNumber(this.configValue('weight', 50), 50, 0, 1000000);
      const starts = parseExplicitDate(this.configValue('startsAt', ''));
      const ends = parseExplicitDate(this.configValue('endsAt', ''));
      this.scheduleBounds = { startsAt: starts.value, endsAt: ends.value };
      this.scheduleValid = starts.valid && ends.valid &&
        !(starts.value !== null && ends.value !== null && starts.value >= ends.value);
      this.idleSeconds = finiteNumber(this.configValue('idleSeconds', 30), 30, 1, 86400);
      this._autoReady = false;
      this.stepName = this.flow === 'two_step' ? 'intro' : null;
      this.dataset.format = this.format;
      this.dataset.flow = this.flow;
      this.classList.add(`lfnp-format-${this.format}`, `lfnp-flow-${this.flow}`);
      this.initializeAudienceViews();
      if (this.flow === 'two_step') {
        const formError = Array.from(this.querySelectorAll('[data-step="form"] [data-result="error"], [data-step="form"] .lfnp-error'))
          .some((node) => !node.hidden && !node.closest('[hidden]') && node.getAttribute('aria-hidden') !== 'true');
        this.setStep(formError ? 'form' : 'intro', false);
      }
      else if (this.flow === 'announcement') this.validateAnnouncementCta();
      this.configureCountdown();
    }

    audienceViews() {
      return Array.from(this.querySelectorAll('[data-audience-view]'));
    }

    initializeAudienceViews() {
      this.serverAudience = String(this.contextValue('audience', '') || '').trim().toLowerCase();
      const views = this.audienceViews();
      const validAudiences = ['prospect', 'first_offer', 'returning'];
      const hasServerAudience = validAudiences.includes(this.serverAudience);
      const hasLocalSubscription = this.hasLocalSubscription();
      let initialAudience = hasServerAudience ? this.serverAudience : (hasLocalSubscription ? 'first_offer' : 'prospect');
      if (this.serverAudience === 'prospect' && hasLocalSubscription) initialAudience = 'first_offer';

      // Older campaign markup has no segmented views. Keep its existing single-view behavior.
      if (!views.length) {
        this.audienceView = hasServerAudience ? this.serverAudience : 'prospect';
        if (hasServerAudience && this.serverAudience !== 'prospect') this.autoSuppressed = true;
        return;
      }

      if (!views.some((view) => view.getAttribute('data-audience-view') === initialAudience)) {
        if (hasServerAudience || hasLocalSubscription) {
          // Fail closed if a known subscriber or buyer has no matching code-only panel. Never
          // fall back to exposing the prospect email form for a known customer state.
          views.forEach((view) => {
            view.hidden = true;
            view.setAttribute('aria-hidden', 'true');
          });
          this.audienceView = '';
          this.audienceViewInvalid = true;
          this.autoSuppressed = true;
          return;
        }
        initialAudience = views.some((view) => view.getAttribute('data-audience-view') === 'prospect')
          ? 'prospect'
          : String(views[0].getAttribute('data-audience-view') || '');
      }

      this.setAudienceView(initialAudience, { silent: true });
      if (hasLocalSubscription) {
        // This browser already completed Shopify's native newsletter form. Keep the return-offer
        // choice available, but never allow a switch back to another email sign-up form.
        this.querySelectorAll('[data-audience-switch="prospect"]').forEach((control) => {
          control.hidden = true;
          control.setAttribute('aria-hidden', 'true');
          control.setAttribute('tabindex', '-1');
        });
      }
    }

    setAudienceView(audience, options = {}) {
      const nextAudience = String(audience || '').trim().toLowerCase();
      const views = this.audienceViews();
      if (!['prospect', 'first_offer', 'returning'].includes(nextAudience) || !views.length) return false;
      if (nextAudience === 'prospect' && this.hasLocalSubscription() && !options.allowSubscribed) return false;
      const target = views.find((view) => view.getAttribute('data-audience-view') === nextAudience);
      if (!target) return false;

      views.forEach((view) => {
        const active = view === target;
        view.hidden = !active;
        view.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      this.audienceView = nextAudience;
      this.syncDialogLabels();

      if (options.focus) {
        let focusTarget = target.querySelector('[data-audience-focus]') || getFirstFocusable(target);
        if (focusTarget && focusTarget.matches('h1, h2, h3, [role="heading"]') && !focusTarget.hasAttribute('tabindex')) {
          focusTarget.setAttribute('tabindex', '-1');
        }
        safeFocus(focusTarget);
      }
      if (!options.silent) this.emitEvent('audience_view', { reason: nextAudience });
      return true;
    }

    hasRenderableAudienceView() {
      if (!this.audienceView) return false;
      const view = this.audienceViews().find((node) => node.getAttribute('data-audience-view') === this.audienceView);
      return Boolean(view && !view.hidden && view.getAttribute('aria-hidden') !== 'true');
    }

    validateAnnouncementCta() {
      this.querySelectorAll('[data-cta]').forEach((cta) => {
        const href = String(cta.getAttribute('href') || '').trim();
        let safe = false;
        if (href) {
          try {
            const url = new URL(href, window.location.href);
            safe = (url.protocol === 'http:' || url.protocol === 'https:') && url.origin === window.location.origin;
          } catch (_error) {
            safe = false;
          }
        }
        if (!safe) {
          cta.hidden = true;
          cta.setAttribute('aria-hidden', 'true');
          cta.setAttribute('tabindex', '-1');
        }
      });
    }

    configureCountdown() {
      const countdown = this.querySelector('[data-countdown]');
      if (!countdown) return;
      if (!asBoolean(this.configValue('showCountdown', false)) || !this.scheduleBounds.endsAt) {
        countdown.hidden = true;
        countdown.setAttribute('aria-hidden', 'true');
      } else {
        countdown.hidden = false;
        countdown.removeAttribute('aria-hidden');
      }
    }

    updateCountdown() {
      const countdown = this.querySelector('[data-countdown]');
      const endsAt = this.scheduleBounds?.endsAt;
      if (!countdown || !endsAt || countdown.hidden) return;
      const remaining = Math.max(0, endsAt - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      countdown.textContent = `${String(days).padStart(2, '0')}:${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      countdown.setAttribute('aria-label', countdown.dataset.countdownLabel || countdown.textContent);
      if (!remaining) {
        countdown.hidden = true;
        countdown.setAttribute('aria-hidden', 'true');
        this.stopCountdown();
      }
    }

    startCountdown() {
      if (this.countdownTimer === null && this.querySelector('[data-countdown]:not([hidden])')) {
        this.updateCountdown();
        this.countdownTimer = window.setInterval(() => this.updateCountdown(), 1000);
      }
    }

    stopCountdown() {
      if (this.countdownTimer !== null) {
        window.clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    }

    setStep(step, announce = true) {
      if (this.flow !== 'two_step') return false;
      const nextStep = step === 'form' ? 'form' : 'intro';
      const steps = Array.from(this.querySelectorAll('[data-step]'));
      if (!steps.length) return false;
      steps.forEach((node) => {
        const visible = node.getAttribute('data-step') === nextStep;
        node.hidden = !visible;
        node.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });
      const changed = this.stepName !== nextStep;
      this.stepName = nextStep;
      this.syncDialogLabels();
      if (changed && announce) this.emitEvent('step', { reason: nextStep });
      if (changed && (this.isOpen() || (this.isInline() && announce))) {
        const stepRoot = this.querySelector(`[data-step="${nextStep}"]`);
        const preferred = stepRoot?.querySelector('[data-email], input[type="email"], input[name="contact[email]"]');
        safeFocus(preferred || getFirstFocusable(stepRoot));
      }
      return true;
    }

    isNonModal() {
      return this.format === 'banner';
    }

    isInline() {
      return this.format === 'embed';
    }

    inlinePanel() {
      return this.querySelector('[data-inline-panel]') || this;
    }

    setInlineVisibility(visible) {
      if (!this.isInline()) return;
      const panel = this.inlinePanel();
      if (!panel) return;
      panel.hidden = !visible;
      panel.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (visible) this.syncDialogLabels();
    }

    isInlinePanelInViewport() {
      const panel = this.inlinePanel();
      if (!panel || !isVisible(panel)) return false;
      const rect = panel.getBoundingClientRect();
      return rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
    }

    recordInlineImpression() {
      if (!this.isInline() || this.inlineImpressionRecorded || document.visibilityState === 'hidden') return;
      if (this.preview) {
        this.inlineImpressionRecorded = true;
        this.teardownInlineImpressionObserver();
        return;
      }
      if (this.isBlockedByThemeUi()) {
        this.scheduleInlineImpressionRetry();
        return;
      }
      if (!this.isTargetingEligible()) return;
      this.inlineImpressionRecorded = true;
      this.teardownInlineImpressionObserver();
      this.emitEvent('impression', { reason: 'visible' });
    }

    checkInlineImpressionVisibility() {
      if (this.isInlinePanelInViewport()) this.recordInlineImpression();
    }

    scheduleInlineImpressionRetry() {
      if (this.inlineImpressionRetryTimer !== null || this.inlineImpressionRecorded) return;
      this.inlineImpressionRetryTimer = window.setTimeout(() => {
        this.inlineImpressionRetryTimer = null;
        if (!this.isConnected || this.inlineImpressionRecorded || !this.isInlinePanelInViewport()) return;
        this.recordInlineImpression();
      }, 500);
    }

    setupInlineImpressionObserver() {
      if (!this.isInline() || this.inlineImpressionRecorded) return;
      const panel = this.inlinePanel();
      if (!panel || !panel.isConnected) return;
      this.teardownInlineImpressionObserver();
      if (typeof window.IntersectionObserver === 'function') {
        this.inlineObserver = new window.IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0)) this.checkInlineImpressionVisibility();
        }, { threshold: [0, 0.01] });
        this.inlineObserver.observe(panel);
        this.inlineVisibilityHandler = () => this.checkInlineImpressionVisibility();
        document.addEventListener('visibilitychange', this.inlineVisibilityHandler, { signal: this.signal });
        return;
      }
      this.inlineVisibilityHandler = () => this.checkInlineImpressionVisibility();
      window.addEventListener('scroll', this.inlineVisibilityHandler, { passive: true, signal: this.signal });
      window.addEventListener('resize', this.inlineVisibilityHandler, { passive: true, signal: this.signal });
      document.addEventListener('visibilitychange', this.inlineVisibilityHandler, { signal: this.signal });
      this.checkInlineImpressionVisibility();
    }

    teardownInlineImpressionObserver() {
      if (this.inlineImpressionRetryTimer !== null) {
        window.clearTimeout(this.inlineImpressionRetryTimer);
        this.inlineImpressionRetryTimer = null;
      }
      if (this.inlineObserver) {
        this.inlineObserver.disconnect();
        this.inlineObserver = null;
      }
      if (this.inlineVisibilityHandler) {
        window.removeEventListener('scroll', this.inlineVisibilityHandler);
        window.removeEventListener('resize', this.inlineVisibilityHandler);
        document.removeEventListener('visibilitychange', this.inlineVisibilityHandler);
        this.inlineVisibilityHandler = null;
      }
    }

    showInlineResult() {
      if (!this.isInline()) return false;
      const result = this.resultNodes().find((node) => !node.hidden);
      if (!result) return false;
      const resultStep = result.closest('[data-step]');
      if (resultStep?.hidden || resultStep?.getAttribute('aria-hidden') === 'true') {
        this.setStep(resultStep.getAttribute('data-step'), false);
      }
      this.setInlineVisibility(true);
      if (this.inlineResultRevealed) return true;
      this.inlineResultRevealed = true;
      if (!result.hasAttribute('tabindex')) result.setAttribute('tabindex', '-1');
      const reveal = () => {
        if (!result.isConnected || result.hidden || result.closest('[hidden]')) return;
        const active = document.activeElement;
        if (active && active !== document.body && active !== document.documentElement && !this.contains(active)) return;
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        try {
          result.scrollIntoView({ block: 'start', inline: 'nearest', behavior: reducedMotion ? 'auto' : 'smooth' });
        } catch (_error) {
          result.scrollIntoView();
        }
        safeFocus(result);
      };
      const scheduleReveal = () => {
        window.setTimeout(() => {
          if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(reveal);
          else reveal();
        }, 0);
      };
      if (document.readyState === 'complete') scheduleReveal();
      else window.addEventListener('load', scheduleReveal, { once: true, signal: this.signal });
      return true;
    }

    usesModalSurface() {
      return !this.isNonModal() && !this.isInline() || Boolean(this.modalOpenForBanner);
    }

    setBannerVisibility(visible) {
      const banner = this.querySelector('[data-banner]');
      if (!banner) return;
      banner.hidden = !visible;
      banner.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    setupControls() {
      const bind = (node, event, handler, options) => {
        if (node) node.addEventListener(event, handler, { ...(options || {}), signal: this.signal });
      };

      bind(document, 'lfnp:event', (event) => this.acceptNewsletterSubscription(event.detail));

      if (!this.isInline()) {
        this.querySelectorAll('[data-open]').forEach((node) => {
          bind(node, 'click', (event) => {
            event.preventDefault();
            this.runtime.openManual(this, event.currentTarget);
          });
        });
        this.querySelectorAll('[data-banner-expand]').forEach((node) => {
          bind(node, 'click', (event) => {
            event.preventDefault();
            this.runtime.openManual(this, event.currentTarget);
          });
        });
      }
      if (!this.isInline()) {
        this.querySelectorAll('[data-close]').forEach((node) => {
          bind(node, 'click', (event) => {
            event.preventDefault();
            this.closeReason = 'close';
            this.close();
          });
        });
      }
      this.querySelectorAll('[data-copy]').forEach((node) => {
        bind(node, 'click', () => this.copyCode(node));
      });
      this.querySelectorAll('[data-audience-switch]').forEach((node) => {
        bind(node, 'click', (event) => {
          event.preventDefault();
          this.setAudienceView(node.getAttribute('data-audience-switch'), { focus: true });
        });
      });
      this.querySelectorAll('[data-next]').forEach((node) => {
        bind(node, 'click', (event) => {
          event.preventDefault();
          this.setStep('form');
        });
      });
      this.querySelectorAll('[data-back]').forEach((node) => {
        bind(node, 'click', (event) => {
          event.preventDefault();
          this.setStep('intro');
        });
      });
      this.querySelectorAll('[data-cta]').forEach((node) => {
        bind(node, 'click', () => this.emitEvent('cta_click'));
      });

      if (this.dialog) {
        bind(this.dialog, 'click', (event) => {
          if (event.target === this.dialog) {
            this.closeReason = 'backdrop';
            this.close();
          }
        });
        bind(this.dialog, 'close', () => this.handleClosed());
        bind(this.dialog, 'cancel', () => {
          this.closeReason = 'escape';
          // Let the native dialog close on Escape. The close event restores focus and scroll.
        });
        bind(this.dialog, 'keydown', (event) => this.handleFallbackFocus(event));
      }
      if (this.isNonModal()) bind(this, 'keydown', (event) => this.handleFallbackFocus(event));

      if (this.form) {
        bind(this.form, 'submit', (event) => this.handleSubmit(event));
      }

      // A cancelled hCaptcha can leave this popup's pending marker in the tab for a short
      // period. Clear it when a separate newsletter form is submitted so that its Liquid
      // result cannot be attributed to this popup. Shopify's /challenge continuation uses
      // #captcha_form and must retain the popup marker until the real form result returns.
      bind(document, 'submit', (event) => {
        const submittedForm = event.target instanceof HTMLFormElement ? event.target : null;
        if (!submittedForm || submittedForm === this.form || this.form?.contains(submittedForm)) return;
        if (this.isExternalNewsletterForm(submittedForm)) this.clearPending();
      }, { capture: true });

      // Close the native dialog before the theme search UI opens. This is a capture listener so
      // it runs before the search drawer's own trigger listener.
      bind(document, 'click', (event) => {
        if (!this.isOpen()) return;
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        const searchTrigger = target.closest('[data-search-toggle], button[aria-label="Search"], a[href="/search"], a[href$="/search"]');
        if (searchTrigger) {
          this.closeReason = 'search';
          this.close();
        }
      }, { capture: true });
    }

    installCompatibilityHook() {
      if (typeof window === 'undefined') return;
      if (!window.lfmDismissEmailPopup || !window.lfmDismissEmailPopup.__lfnpNative) {
        const dismiss = () => {
          document.querySelectorAll(ELEMENT_NAME).forEach((root) => {
            if (typeof root.dismissForCart === 'function') root.dismissForCart();
          });
        };
        dismiss.__lfnpNative = true;
        window.lfmDismissEmailPopup = dismiss;
      }
    }

    isHomepage() {
      return asBoolean(this.dataset.homepage) || this.dataset.pageType === 'index';
    }

    isDeviceEligible() {
      const device = String(this.dataset.device || 'both').toLowerCase();
      const mobile = window.matchMedia ? window.matchMedia('(max-width: 749px)').matches : false;
      if (device === 'desktop') return !mobile;
      if (device === 'mobile') return mobile;
      return true;
    }

    isCustomerLoggedIn() {
      return asBoolean(this.contextValue('customerLoggedIn', false)) ||
        asBoolean(document.body?.dataset.customerLoggedIn) ||
        asBoolean(document.documentElement?.dataset.customerLoggedIn) ||
        Boolean(window.Shopify?.customer?.id);
    }

    isTargetingEligible(options = {}) {
      if (this.configInvalid || !this.scheduleValid) return false;
      if (!this.isBaseTargetingEligible(options)) return false;
      if (!this.matchesExperiment(options)) return false;
      return true;
    }

    isBaseTargetingEligible(options = {}) {
      const now = Date.now();
      const startsAt = this.scheduleBounds?.startsAt;
      const endsAt = this.scheduleBounds?.endsAt;
      const inline = this.isInline();
      if ((startsAt !== null && startsAt > now) || (endsAt !== null && endsAt <= now)) return false;
      if (!this.isDeviceEligible()) return false;
      if (this.isSuppressed({
        ignoreFrequency: inline || Boolean(options.ignoreFrequency),
        ignorePending: inline || Boolean(options.ignorePending),
      }) || this.isCustomerMarketingSuppressed()) return false;
      if (!this.matchesScopeAndPaths(options)) return false;

      const visitor = String(this.configValue('visitor', 'all') || 'all').toLowerCase();
      if (visitor !== 'all' && visitor !== this.runtime.visitorType) return false;

      const customer = String(this.configValue('customer', 'all') || 'all').toLowerCase();
      const loggedIn = asBoolean(this.contextValue('customerLoggedIn', false));
      if (customer === 'guest' && loggedIn) return false;
      if (customer === 'logged_in' && !loggedIn) return false;

      const countries = splitValues(this.configValue('countries', '')).map((item) => item.toUpperCase());
      const country = String(this.contextValue('country', '') || '').toUpperCase();
      if (countries.length && (!country || !countries.includes(country))) return false;

      const languages = splitValues(this.configValue('languages', '')).map((item) => item.toLowerCase());
      const language = String(this.contextValue('language', '') || '').toLowerCase();
      if (languages.length && (!language || !languages.some((item) => language === item || language.startsWith(`${item}-`)))) return false;

      const minViews = finiteNumber(this.configValue('minViews', 1), 1, 1, 1000000);
      if (this.runtime.viewCount < minViews) return false;

      const maxSessionShows = finiteNumber(this.configValue('maxSessionShows', 1), 1, 0, 1000000);
      if (!inline && !options.ignoreSession && maxSessionShows > 0 && this.sessionShowCount() >= maxSessionShows) return false;

      const cartMinItems = finiteNumber(this.configValue('cartMinItems', 0), 0, 0, 1000000);
      const cartItems = Number(this.contextValue('cartItems', 0));
      if (Number.isFinite(cartItems) && cartItems < cartMinItems) return false;
      if (!Number.isFinite(cartItems) && cartMinItems > 0) return false;

      const utmSources = splitValues(this.configValue('utmSources', '')).map((item) => item.toLowerCase());
      if (utmSources.length) {
        let source = String(this.contextValue('utmSource', '') || '');
        try {
          const querySource = new URLSearchParams(window.location.search).get('utm_source');
          if (querySource) source = querySource;
        } catch (_error) { /* Liquid context remains the fallback. */ }
        if (!source || !utmSources.includes(source.toLowerCase())) return false;
      }

      const referrerHosts = splitValues(this.configValue('referrerHosts', '')).map((item) => item.toLowerCase());
      if (referrerHosts.length) {
        let host = String(this.contextValue('referrerHost', '') || '').toLowerCase();
        try {
          if (!host && document.referrer) host = new URL(document.referrer).hostname.toLowerCase();
        } catch (_error) { host = ''; }
        if (!host || !referrerHosts.some((item) => host === item || host.endsWith(`.${item}`))) return false;
      }
      return true;
    }

    isCustomerMarketingSuppressed() {
      // Liquid supplies these audiences from the signed-in Shopify customer record. Buyers and
      // existing subscribers with no orders receive a code-only view, so consent must not hide it.
      if ((this.serverAudience === 'returning' || this.serverAudience === 'first_offer') && this.hasRenderableAudienceView()) return false;
      return this.isCustomerMarketingFlagged();
    }

    isCustomerMarketingFlagged() {
      return asBoolean(this.contextValue('customerMarketing', false)) ||
        Boolean(window.Shopify?.customer?.accepts_marketing);
    }

    matchesScopeAndPaths(options = {}) {
      const hasExplicitScope = this.config && Object.prototype.hasOwnProperty.call(this.config, 'scope') ||
        typeof this.dataset.scope !== 'undefined';
      const scope = String(this.configValue('scope', options.manual && !hasExplicitScope ? 'all' : 'homepage') || 'homepage').toLowerCase();
      const pageType = String(this.contextValue('pageType', '') || '').toLowerCase();
      const homepage = this.isHomepage();
      const path = String(window.location.pathname || '/');
      if (scope === 'homepage' && !homepage) return false;
      if (scope === 'products' && pageType !== 'product') return false;
      if (scope === 'collections' && pageType !== 'collection') return false;
      if (!['homepage', 'all', 'products', 'collections', 'custom'].includes(scope)) return false;
      const includes = splitValues(this.configValue('includePaths', ''));
      const excludes = splitValues(this.configValue('excludePaths', ''));
      if (excludes.some((pattern) => wildcardMatches(path, pattern))) return false;
      if (scope === 'custom' && !includes.length) return false;
      if (includes.length && !includes.some((pattern) => wildcardMatches(path, pattern))) return false;
      return true;
    }

    matchesExperiment(options = {}) {
      if (this.isInline()) return true;
      if (!this.experimentName) return true;
      const siblings = Array.from(this.runtime.roots)
        .filter((root) => root.experimentName === this.experimentName && root.isBaseTargetingEligible(options))
        .map((root) => ({ variant: root.experimentVariant, weight: root.experimentWeight }));
      const assigned = this.runtime.experimentAssignment(this.experimentName, siblings);
      return Boolean(assigned && assigned === this.experimentVariant);
    }

    canAutoOpen() {
      return !this.isInline() && !this.preview && !this.autoSuppressed && this._autoReady && this.isTargetingEligible();
    }

    canManualOpen() {
      if (this.isInline()) return false;
      if (this.preview) return !this.configInvalid && this.scheduleValid;
      if (this.autoSuppressed) return false;
      return this.isTargetingEligible({
        manual: true,
        // A launcher is an explicit user action. It can be used to retry after a cancelled
        // hCaptcha or a dismissed campaign, while page/device/audience/schedule rules remain.
        ignoreFrequency: true,
        ignorePending: true,
        ignoreSession: true,
      });
    }

    setLauncherVisibility(visible) {
      if (this.isInline()) return;
      const launchers = Array.from(this.querySelectorAll('[data-open]:not([data-banner-expand])'))
        .filter((launcher) => !launcher.closest('[data-banner]'));
      launchers.forEach((launcher, index) => {
        if (!launcher.__lfnpOriginalLauncherState) {
          launcher.__lfnpOriginalLauncherState = {
            hidden: launcher.hidden,
            ariaHidden: launcher.getAttribute('aria-hidden'),
            tabindex: launcher.getAttribute('tabindex'),
          };
        }
        const shouldShow = Boolean(visible && index === 0);
        if (shouldShow) {
          const original = launcher.__lfnpOriginalLauncherState;
          launcher.hidden = Boolean(original.hidden);
          if (original.ariaHidden === null) launcher.removeAttribute('aria-hidden');
          else launcher.setAttribute('aria-hidden', original.ariaHidden);
          if (original.tabindex === null) launcher.removeAttribute('tabindex');
          else launcher.setAttribute('tabindex', original.tabindex);
        } else {
          launcher.hidden = true;
          launcher.setAttribute('aria-hidden', 'true');
          launcher.setAttribute('tabindex', '-1');
        }
      });
      if (this.isNonModal() && !this.isOpen()) {
        const hasExternalLauncher = launchers.length > 0;
        if (!hasExternalLauncher && visible && this.trigger === 'manual') this.setBannerVisibility(true);
        else if (!visible) this.setBannerVisibility(false);
      }
    }

    sessionShowCount() {
      const record = readJsonStorage('session', this.sessionShowKey(), { count: 0 });
      return Math.max(0, Number(record?.count) || 0);
    }

    recordSessionShow(audience) {
      const key = this.sessionShowKey(audience);
      const record = readJsonStorage('session', key, { count: 0 });
      const count = Math.max(0, Number(record?.count) || 0) + 1;
      writeJsonStorage('session', key, { count, at: Date.now() });
    }

    emitEvent(eventName, values) {
      if (this.preview || typeof document.dispatchEvent !== 'function') return;
      const reason = values?.reason ? String(values.reason).replace(/[^a-z0-9_-]/gi, '').slice(0, 40) : undefined;
      const detail = {
        event: String(eventName),
        campaign: this.campaign,
        audience: this.audienceView || this.serverAudience || 'prospect',
        experiment: this.experimentName || '',
        variant: this.experimentName ? this.experimentVariant : '',
        format: this.format,
        trigger: this.trigger,
        timestamp: new Date().toISOString(),
      };
      if (reason) detail.reason = reason;
      document.dispatchEvent(new CustomEvent('lfnp:event', { detail }));
    }

    isExternalNewsletterForm(form) {
      if (!form || form.id === 'captcha_form' || form.closest('#captcha_form')) return false;
      if (form.closest(ELEMENT_NAME)) return false;
      const formType = form.querySelector('input[name="form_type"]')?.value?.trim().toLowerCase();
      if (formType !== 'customer') return false;
      if (!form.querySelector('input[name="contact[email]"], input[type="email"]')) return false;

      const knownNewsletterForm = form.matches(
        '.lfm-footer-nl-form, .newsletter-form, .section-newsletter form, .lf-footer-newsletter form, [class*="lfm-newsletter-"][class*="__form"]'
      );
      const newsletterTags = form.querySelector('input[name="contact[tags]"]')?.value || '';
      return knownNewsletterForm || /(^|[,\s])newsletter([,\s]|$)/i.test(newsletterTags);
    }

    isPopupResult(node) {
      if (!node) return false;
      const resultCampaign = node.getAttribute('data-campaign');
      return !resultCampaign || resultCampaign === this.campaign;
    }

    resultNodes() {
      return Array.from(this.querySelectorAll('[data-result="success"], [data-result="error"]'))
        .filter((node) => this.isPopupResult(node));
    }

    hasResult() {
      return this.resultNodes().length > 0;
    }

    hasRenderableResult() {
      return this.handledResult && this.resultNodes().some((node) =>
        !node.hidden && !node.closest('[hidden]') && !node.closest('[aria-hidden="true"]') && !node.closest('[inert]')
      );
    }

    prepareServerResult() {
      const results = this.resultNodes();
      if (!results.length) return;
      const pending = this.readPending();
      const success = results.find((node) => node.getAttribute('data-result') === 'success');
      const error = results.find((node) => node.getAttribute('data-result') === 'error');

      // Shopify may render a customer form result for a footer form on the same page. A result
      // is trusted only when this tab has a fresh marker created by this popup's own form.
      if (!pending || !this.isPendingFresh(pending) || !this.returnFragmentMatches()) {
        results.forEach((node) => {
          node.hidden = true;
          node.setAttribute('aria-hidden', 'true');
        });
        this.setSignupVisible(true);
        this.syncDialogLabels();
        return;
      }

      // A verified Shopify response belongs to the prospect signup that submitted the form,
      // even if Liquid now reports the customer as a first-offer subscriber after the POST.
      this.setAudienceView('prospect', { silent: true, allowSubscribed: true });

      results.forEach((node) => {
        const visible = node === (success || error);
        node.hidden = !visible;
        node.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });
      this.setSignupVisible(!success);
      this.syncDialogLabels();
      this.handledResult = true;
      if (this.isInline()) this.runtime.consumeAutomaticOpportunity(this);
      this.clearPending();
      if (success && success === (success || error)) {
        this.remember('subscribed');
        const successCodeNode = success.querySelector('[data-code]');
        const firstOfferCodeNode = this.querySelector('[data-audience-view="first_offer"] [data-code]');
        const successCode = String(successCodeNode?.value || successCodeNode?.textContent || '').trim().toUpperCase();
        const firstOfferCode = String(firstOfferCodeNode?.value || firstOfferCodeNode?.textContent || '').trim().toUpperCase();
        if (successCode && firstOfferCode && successCode === firstOfferCode) {
          // The trusted success view has already delivered this code. Record the matching
          // first-offer exposure so the post-submit shop CTA does not reopen it on navigation.
          this.recordSessionShow('first_offer');
          this.remember('dismissed', 'first_offer');
        }
        this.emitEvent('subscribe_success');
      } else if (error) {
        this.emitEvent('subscribe_error');
      }
      if (this.isInline()) this.showInlineResult();
    }

    setSignupVisible(visible) {
      if (!this.signup) return;
      this.signup.hidden = !visible;
      this.signup.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    syncDialogLabels() {
      const labelTarget = this.dialog || (this.isInline() ? this.inlinePanel() : null);
      if (!labelTarget) return;
      const labelScope = labelTarget;
      const visibleResult = Array.from(labelScope.querySelectorAll('[data-result="success"], [data-result="error"]'))
        .find((node) => !node.hidden && !node.closest('[hidden]') && !node.closest('[aria-hidden="true"]'));
      const visibleTitle = Array.from((visibleResult || labelScope).querySelectorAll('h1[id], h2[id], h3[id], [role="heading"][id]'))
        .find((node) => !node.hidden && !node.closest('[hidden]') && !node.closest('[aria-hidden="true"]'));
      const visibleDescription = Array.from((visibleResult || labelScope).querySelectorAll('[data-description][id], .lfnp-description[id], p[id]'))
        .find((node) => !node.hidden && !node.closest('[hidden]') && !node.closest('[aria-hidden="true"]'));
      if (visibleTitle?.id) labelTarget.setAttribute('aria-labelledby', visibleTitle.id);
      else labelTarget.removeAttribute('aria-labelledby');
      if (visibleDescription?.id) labelTarget.setAttribute('aria-describedby', visibleDescription.id);
      else labelTarget.removeAttribute('aria-describedby');
    }

    scheduleAutoTrigger() {
      this.clearAutoTrigger();
      if (this.isInline()) return;
      this.trigger = this.triggerName();
      if (!this.isTargetingEligible()) return;
      if (this.trigger === 'scroll') {
        this.scrollHandler = () => {
          if (this.scrollThresholdReached()) {
            this.clearAutoTrigger();
            this.markTriggerReady();
          }
        };
        window.addEventListener('scroll', this.scrollHandler, { passive: true, signal: this.signal });
        window.addEventListener('resize', this.scrollHandler, { passive: true, signal: this.signal });
        this.scrollHandler();
        return;
      }
      if (this.trigger === 'delay') {
        const delaySeconds = finiteNumber(this.configValue('delay', DEFAULT_DELAY_SECONDS), DEFAULT_DELAY_SECONDS, 0, 86400);
        this.timer = window.setTimeout(() => this.markTriggerReady(), delaySeconds * 1000);
        return;
      }
      if (this.trigger === 'exit') {
        this.exitHandler = (event) => {
          if (window.matchMedia?.('(max-width: 749px)').matches) return;
          if (event.clientY <= 0 && !event.relatedTarget) {
            this.clearAutoTrigger();
            this.markTriggerReady();
          }
        };
        document.addEventListener('mouseout', this.exitHandler, { signal: this.signal });
        return;
      }
      if (this.trigger === 'idle') {
        this.idleHandler = () => {
          if (this.idleTimer !== null) window.clearTimeout(this.idleTimer);
          this._autoReady = false;
          this.idleTimer = window.setTimeout(() => this.markTriggerReady(), this.idleSeconds * 1000);
        };
        ['mousemove', 'keydown', 'pointerdown', 'scroll', 'touchstart'].forEach((eventName) => {
          window.addEventListener(eventName, this.idleHandler, { passive: eventName !== 'keydown', signal: this.signal });
        });
        this.idleHandler();
      }
    }

    markTriggerReady() {
      if (this.isInline()) return;
      this._autoReady = true;
      this.runtime.requestReadyCampaigns();
    }

    clearAutoTrigger() {
      if (this.timer !== null) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
      if (this.retryTimer !== null) {
        window.clearTimeout(this.retryTimer);
        this.retryTimer = null;
      }
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
        window.removeEventListener('resize', this.scrollHandler);
        this.scrollHandler = null;
      }
      if (this.exitHandler) {
        document.removeEventListener('mouseout', this.exitHandler);
        this.exitHandler = null;
      }
      if (this.idleHandler) {
        ['mousemove', 'keydown', 'pointerdown', 'scroll', 'touchstart'].forEach((eventName) => {
          window.removeEventListener(eventName, this.idleHandler);
        });
        this.idleHandler = null;
      }
      if (this.idleTimer !== null) {
        window.clearTimeout(this.idleTimer);
        this.idleTimer = null;
      }
      this._autoReady = false;
    }

    scrollThresholdReached() {
      const available = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      if (!available) return false;
      const percent = finiteNumber(this.configValue('scroll', DEFAULT_SCROLL_PERCENT), DEFAULT_SCROLL_PERCENT, 1, 100);
      return (window.scrollY / available) * 100 >= percent;
    }

    tryAutoOpen() {
      this.markTriggerReady();
    }

    openBannerForm(trigger) {
      if (!this.isNonModal() || this.flow === 'announcement') return this.open(trigger, { manual: true });
      if (this.nonModalOpen) {
        // The banner is the non-modal teaser. A user click can promote it to the native
        // dialog containing the real form; this transition does not represent a dismiss.
        this.setBannerVisibility(false);
        this.nonModalOpen = false;
        this.previousOverflow = null;
        this.dialog?.classList.remove('lfnp-dialog--open');
        this.stopCountdown();
        this._automaticOpen = false;
        this.closeReason = null;
      }
      return this.open(trigger, { manual: true, forceDialog: true });
    }

    open(trigger, options) {
      const automatic = Boolean(options?.automatic);
      if (this.isInline()) return false;
      if ((!this.dialog && !this.isNonModal()) || this.isOpen() || document.visibilityState === 'hidden') return false;
      if (automatic && !this.canAutoOpen()) return false;
      if (this.isBlockedByThemeUi()) {
        if (automatic) this.runtime.requestReadyCampaigns();
        return false;
      }
      this.clearAutoTrigger();
      this._automaticOpen = automatic;
      this.closeReason = null;
      const modalSurface = !this.isNonModal() || Boolean(options?.forceDialog);
      if (modalSurface && !this.dialog) return false;
      this.modalOpenForBanner = Boolean(this.isNonModal() && modalSurface);
      this.previousFocus = modalSurface ? (trigger || document.activeElement) : null;
      this.previousOverflow = {
        html: document.documentElement.style.overflow,
        body: document.body.style.overflow,
      };
      if (modalSurface) {
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        if (typeof this.dialog.showModal === 'function') {
          try {
            this.dialog.showModal();
          } catch (_error) {
            this.dialog.setAttribute('open', '');
            this.fallbackOpen = true;
          }
        } else {
          this.dialog.setAttribute('open', '');
          this.fallbackOpen = true;
        }
      } else {
        this.setBannerVisibility(true);
        this.nonModalOpen = true;
      }
      this.dialog?.classList.add('lfnp-dialog--open');
      this.syncDialogLabels();
      const audienceFocus = modalSurface && ['returning', 'first_offer'].includes(this.audienceView)
        ? this.querySelector(`[data-audience-view="${this.audienceView}"] [data-audience-focus]`)
        : null;
      if (audienceFocus) {
        if (!audienceFocus.hasAttribute('tabindex')) audienceFocus.setAttribute('tabindex', '-1');
        safeFocus(audienceFocus);
      } else if (modalSurface && this.flow === 'two_step' && this.stepName === 'form') {
        const formStep = this.querySelector('[data-step="form"]');
        const email = formStep?.querySelector('[data-email], input[type="email"], input[name="contact[email]"]');
        safeFocus(email || getFirstFocusable(formStep));
      } else if (modalSurface && this.fallbackOpen) {
        safeFocus(getFirstFocusable(this.dialog));
      }
      if (automatic) this.recordSessionShow();
      this.startCountdown();
      this.emitEvent('open', { reason: automatic ? 'automatic' : 'manual' });
      this.emitEvent('impression', { reason: automatic ? 'automatic' : 'manual' });
      return true;
    }

    close() {
      if (this.isInline()) return;
      if (!this.isOpen()) return;
      if (!this.closeReason) this.closeReason = 'dismiss';
      if (this.nonModalOpen || this.fallbackOpen || !this.dialog || typeof this.dialog.close !== 'function') {
        this.dialog?.removeAttribute('open');
        this.fallbackOpen = false;
        this.nonModalOpen = false;
        this.handleClosed();
        return;
      }
      this.dialog.close();
    }

    isOpen() {
      return !this.isInline() && Boolean(this.nonModalOpen || (this.dialog && (this.dialog.open || this.fallbackOpen)));
    }

    dismissForCart() {
      this.autoSuppressed = true;
      this.clearAutoTrigger();
      this.closeReason = 'cart';
      if (this.isOpen()) this.close();
      this.runtime.markAutomaticDismissed();
      this.runtime.syncLaunchers();
    }

    handleClosed() {
      if (!this.previousOverflow) return;
      const overflow = this.previousOverflow;
      const restorePageScroll = () => {
        if (this.isOpen() || this.previousOverflow) return;
        if (overflow.html) document.documentElement.style.overflow = overflow.html;
        else document.documentElement.style.removeProperty('overflow');
        if (this.isThemeScrollLocked()) {
          // Cart and search scripts own the body lock. A native dialog close can be delivered
          // before their open class is applied, so re-check in the next task as well.
          document.body.style.overflow = 'hidden';
        } else if (overflow.body) document.body.style.overflow = overflow.body;
        else document.body.style.removeProperty('overflow');
      };
      this.previousOverflow = null;
      if (this.usesModalSurface()) {
        restorePageScroll();
        window.setTimeout(restorePageScroll, 0);
      }
      if (this.isNonModal()) this.setBannerVisibility(false);
      this.dialog?.classList.remove('lfnp-dialog--open');
      this.stopCountdown();
      const focus = this.previousFocus;
      this.previousFocus = null;
      if (focus && focus.isConnected && !focus.hasAttribute('disabled')) safeFocus(focus);
      if (this._automaticOpen) this.runtime.markAutomaticDismissed();
      const reason = this.closeReason || 'dismiss';
      if (reason !== 'submit') this.emitEvent('dismiss', { reason });
      this._automaticOpen = false;
      this.modalOpenForBanner = false;
      this.closeReason = null;
      // Submitting begins Shopify's native navigation or hCaptcha challenge. It is not a
      // dismissal, and a failed/cancelled challenge must not create a week-long suppression.
      if (reason !== 'submit') this.remember('dismissed');
      this.runtime?.roots.forEach((root) => {
        if (root !== this && root.isInline()) root.checkInlineImpressionVisibility();
      });
    }

    isThemeScrollLocked() {
      return document.body?.classList.contains('lfm-cart-open') ||
        Boolean(document.querySelector('#lfm-cart-drawer.is-open')) ||
        Boolean(document.querySelector('#lfm-search-overlay.is-open'));
    }

    handleFallbackFocus(event) {
      if (event.key === 'Escape' && (this.fallbackOpen || this.nonModalOpen)) {
        event.preventDefault();
        this.closeReason = 'escape';
        this.close();
        return;
      }
      if (!this.fallbackOpen || !this.dialog || event.key !== 'Tab') return;
      const focusable = Array.from(this.dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(isVisible);
      if (!focusable.length) {
        event.preventDefault();
        safeFocus(this.dialog);
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        safeFocus(last);
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        safeFocus(first);
      }
    }

    handleSubmit(event) {
      const consent = this.form?.querySelector(
        '[data-consent], input[type="checkbox"][name*="accepts_marketing"], input[type="checkbox"][name="contact[lfm_marketing_consent]"]'
      );
      if (consent && !consent.checked) {
        event.preventDefault();
        consent.setAttribute('aria-invalid', 'true');
        safeFocus(consent);
        return;
      }
      if (consent) consent.removeAttribute('aria-invalid');
      // The default browser action continues with Shopify's native POST. We only record a
      // non-PII marker first, then close the top-layer dialog so Shopify's hCaptcha challenge,
      // which is injected outside this element, remains interactive. A returned Liquid form
      // result is still the only success signal.
      this.emitEvent('submit_attempt');
      this.runtime.clearOtherPending(this);
      this.writePending();
      this.closeReason = 'submit';
      this.close();
    }

    async copyCode(button = null) {
      const view = button?.closest('[data-audience-view]') ||
        this.querySelector(`[data-audience-view="${this.audienceView}"]`) || this;
      const codeNode = view.querySelector('[data-code]');
      const status = view.querySelector('[data-copy-status]');
      const code = codeNode?.value || codeNode?.textContent?.trim() || '';
      if (!code) return;
      const copyButton = button || view.querySelector('[data-copy]');
      let copyTimer;
      if (copyButton) copyButton.disabled = true;
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
        await Promise.race([
          navigator.clipboard.writeText(code),
          new Promise((_, reject) => {
            copyTimer = window.setTimeout(() => reject(new Error('Clipboard timed out')), 2000);
          }),
        ]);
        if (status) status.textContent = this.dataset.copied || '';
        this.emitEvent('code_copy');
      } catch (_error) {
        if (status) status.textContent = this.dataset.copyFallback || '';
      } finally {
        window.clearTimeout(copyTimer);
        if (copyButton) copyButton.disabled = false;
      }
    }

    storageValue(storageName, key) {
      let storage = null;
      try {
        storage = storageName === 'local' ? window.localStorage : window.sessionStorage;
      } catch (_error) {
        storage = null;
      }
      return readStorage(storage, key);
    }

    setStorageValue(storageName, key, value) {
      let storage = null;
      try {
        storage = storageName === 'local' ? window.localStorage : window.sessionStorage;
      } catch (_error) {
        storage = null;
      }
      return writeStorage(storage, key, value);
    }

    removeStorageValue(storageName, key) {
      let storage = null;
      try {
        storage = storageName === 'local' ? window.localStorage : window.sessionStorage;
      } catch (_error) {
        storage = null;
      }
      removeStorage(storage, key);
    }

    readPending() {
      const sessionRecord = parseRecord(this.storageValue('session', this.pendingKey));
      const cookieRecord = parseRecord(getCookie(this.pendingCookie));
      const memoryRecord = parseRecord(MEMORY_SESSION[this.pendingKey]);
      return sessionRecord || cookieRecord || memoryRecord;
    }

    isPendingFresh(record) {
      const timestamp = Number(record?.at);
      const now = Date.now();
      return record?.campaign === this.campaign && Number.isFinite(timestamp) && timestamp <= now && timestamp > now - PENDING_TTL_MS;
    }

    returnFragmentMatches() {
      const formId = this.form?.id;
      const hash = String(window.location.hash || '').replace(/^#/, '');
      if (!formId || !hash) return true;
      try {
        return decodeURIComponent(hash) === formId;
      } catch (_error) {
        return hash === formId;
      }
    }

    writePending() {
      const record = JSON.stringify({ campaign: this.campaign, at: Date.now(), status: 'pending' });
      const written = this.setStorageValue('session', this.pendingKey, record);
      MEMORY_SESSION[this.pendingKey] = record;
      setCookie(this.pendingCookie, record, Math.ceil(PENDING_TTL_MS / 1000));
      return written;
    }

    clearPending() {
      this.removeStorageValue('session', this.pendingKey);
      delete MEMORY_SESSION[this.pendingKey];
      deleteCookie(this.pendingCookie);
    }

    audienceStorageKey(audience = this.audienceView || this.serverAudience || 'prospect') {
      const normalized = ['returning', 'first_offer'].includes(audience) ? audience : 'prospect';
      return normalized === 'prospect' ? this.localKey : `${this.localKey}:audience:${normalized}`;
    }

    acceptNewsletterSubscription(detail) {
      if (this.campaign !== 'firstfetch-newsletter-popup' || detail?.event !== 'subscribe_success' ||
          detail.format !== 'embed' || !['newsletter-home', 'newsletter-footer'].includes(detail.campaign)) return;
      this.remember('subscribed', 'prospect');
      if (this.serverAudience === 'returning' || this.audienceView === 'returning') return;
      this.setAudienceView('first_offer', { silent: true });
      this.remember('dismissed', 'first_offer');
      this.autoSuppressed = true;
      this.runtime.syncLaunchers();
    }

    sessionShowKey(audience = this.audienceView || this.serverAudience || 'prospect') {
      const normalized = ['returning', 'first_offer'].includes(audience) ? audience : 'prospect';
      return normalized === 'prospect'
        ? `lfnp:shows:${this.campaign}`
        : `lfnp:shows:${this.campaign}:${normalized}`;
    }

    localRecordForAudience(audience) {
      const key = this.audienceStorageKey(audience);
      const record = parseRecord(this.storageValue('local', key));
      if (this.campaign === 'firstfetch-newsletter-popup' && record?.state === 'dismissed' &&
          record.frequencyDays === undefined && Number.isFinite(Number(record.until)) &&
          Number(this.configValue('frequency', DEFAULT_FREQUENCY_DAYS)) === 3) {
        record.until = Number(record.until) - 4 * 86400000;
        record.frequencyDays = 3;
        this.setStorageValue('local', key, JSON.stringify(record));
      }
      return record;
    }

    localRecord() {
      return this.localRecordForAudience(this.audienceView || this.serverAudience || 'prospect');
    }

    hasLocalSubscription() {
      const record = this.localRecordForAudience('prospect');
      return record?.state === 'subscribed' && Number(record.until) > Date.now();
    }

    isSuppressed(options = {}) {
      const offerView = this.hasRenderableAudienceView() &&
        (this.audienceView === 'returning' || this.audienceView === 'first_offer');
      const pending = this.readPending();
      const pendingFresh = pending && this.isPendingFresh(pending);
      if (pendingFresh && !options.ignorePending && !offerView) return true;
      if (pending && !pendingFresh) this.clearPending();
      const subscribedInProspect = this.hasLocalSubscription();
      if (subscribedInProspect && !offerView) return true;

      const record = this.localRecord();
      if (!record) return false;
      const until = Number(record.until);
      if (record.state === 'subscribed' && Number.isFinite(until) && until > Date.now() && !offerView) return true;
      if (!options.ignoreFrequency && Number.isFinite(until) && until > Date.now()) return true;
      if (options.ignoreFrequency) return false;
      this.removeStorageValue('local', this.audienceStorageKey());
      return false;
    }

    remember(state, audience) {
      if (this.preview) return;
      const targetAudience = audience || this.audienceView || this.serverAudience || 'prospect';
      const previous = this.localRecordForAudience(targetAudience);
      if (state === 'dismissed' && previous?.state === 'subscribed' && Number(previous.until) > Date.now()) return;
      const days = state === 'subscribed' ? SUBSCRIBED_TTL_DAYS : finiteNumber(this.configValue('frequency', DEFAULT_FREQUENCY_DAYS), DEFAULT_FREQUENCY_DAYS, 1, 3650);
      const nextUntil = Date.now() + days * 86400000;
      const previousUntil = Number(previous?.until);
      const until = state === 'subscribed' && Number.isFinite(previousUntil) ? Math.max(previousUntil, nextUntil) : nextUntil;
      this.setStorageValue('local', this.audienceStorageKey(targetAudience), JSON.stringify({ state, until, frequencyDays: days }));
    }

    isBlockedByThemeUi() {
      if (document.body?.classList.contains('lfm-cart-open')) return true;
      const search = document.querySelector('#lfm-search-overlay.is-open');
      if (search) return true;

      // Shopify's consent banner is an alertdialog in the development theme. Do not place a
      // marketing dialog over it while it is visible.
      const cookieBannerVisible = Array.from(document.querySelectorAll(
        '#shopify-pc__banner.shopify-pc__banner__dialog, [role="alertdialog"]'
      )).some((element) => {
        if (!element || !element.isConnected || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
        const style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse';
      });
      if (cookieBannerVisible) return true;

      const openDialog = document.querySelector('dialog[open]');
      if (openDialog && openDialog !== this.dialog) return true;
      const otherModal = Array.from(document.querySelectorAll('[role="dialog"][aria-modal="true"]'))
        .find((node) => node !== this.dialog && !node.hasAttribute('inert') && isVisible(node));
      return Boolean(otherModal);
    }

    openWhenAvailable() {
      if (!this.hasRenderableResult()) return;
      if (this.isBlockedByThemeUi()) {
        this.retryTimer = window.setTimeout(() => this.openWhenAvailable(), 1000);
        return;
      }
      this.runtime.roots.forEach((candidate) => {
        if (candidate !== this && candidate.isOpen()) {
          candidate.closeReason = 'campaign';
          candidate.close();
        }
      });
      const opened = this.isNonModal() && this.flow !== 'announcement'
        ? this.open(undefined, { automatic: false, forceDialog: true })
        : this.open(undefined, { automatic: false });
      if (opened) this.runtime.autoConsumed = true;
    }

    disconnectedCallback() {
      this.clearAutoTrigger();
      this.stopCountdown();
      this.teardownInlineImpressionObserver();
      this.runtime?.unregister(this);
      this.controller?.abort();
      if (this.isOpen()) {
        if (this.fallbackOpen || this.nonModalOpen) this.dialog?.removeAttribute('open');
        else if (typeof this.dialog?.close === 'function') this.dialog.close();
        this.fallbackOpen = false;
        this.nonModalOpen = false;
      }
      this.handleClosed();
      this.modalOpenForBanner = false;
    }
  }

  if (window.customElements && !customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, LfmNewsletterPopup);
  }
})();
